import { mdiClose, mdiImageOutline, mdiMagnify, mdiMenuDown, mdiMessageTextOutline, mdiTextBoxOutline } from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { ActionsStrip } from 'components/ActionsStrip'
import ConfirmDialog from 'components/ConfirmDialog'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Subject } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { removeAIChat } from 'services/compose'
import { deleteAIConversation, fetchAIConversations } from 'services/compose/suggest/actions'
import { AIConversation, AIConversationKind } from 'services/compose/suggest/net'
import { StoreState, StoreThunkDispatch } from 'store/state'
import styles from './AIConversationsList.pcss'

interface AIConversationsListProps {
  className?: string
  onConversationSelect?: (conversation: AIConversation) => void
  onNewChat?: (kind: AIConversationKind) => void
}

export function AIConversationsList({ className, onConversationSelect, onNewChat }: AIConversationsListProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const conversations = useSelector((s: StoreState) => s.composer.aiChats)
  const [loading, setLoading] = React.useState(conversations.length === 0)
  const [filter, setFilter] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [createAnchorEl, setCreateAnchorEl] = React.useState<HTMLElement | null>(null)
  const delete$ = React.useRef(new Subject<string>())

  const openCreateMenu = (e: React.MouseEvent<HTMLElement>) => setCreateAnchorEl(e.currentTarget)
  const closeCreateMenu = () => setCreateAnchorEl(null)
  const startNewChat = (kind: AIConversationKind) => () => {
    closeCreateMenu()
    onNewChat?.(kind)
  }

  const fetchChats = React.useCallback(() => {
    setError(null)
    dispatch(fetchAIConversations())
      .unwrap()
      .catch((error: any) => {
        console.error('Failed to fetch AI conversations:', error)
        setError('Failed to load conversations.')
      })
      .finally(() => setLoading(false))
  }, [dispatch])

  React.useEffect(() => {
    fetchChats()
  }, [fetchChats])

  React.useEffect(() => {
    const sub = delete$.current.pipe(
      switchMap((id: string) => {
        dispatch(removeAIChat(id))
        return dispatch(deleteAIConversation(id))
      })
    ).subscribe()
    return () => sub.unsubscribe()
  }, [dispatch])

  const firstUserMessage = (c: AIConversation) => {
    const msg = c.messages.find((m) => m.role === 'user')
    return msg && msg.kind === 'text' ? msg.text : ''
  }

  const filtered = filter.trim()
    ? conversations.filter((c) => firstUserMessage(c).toLowerCase().includes(filter.toLowerCase()))
    : conversations

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <div className={styles.actions}>
        {onNewChat && (
          <>
            <Button
              className={styles.newChatBtn}
              aria-haspopup="true"
              aria-expanded={Boolean(createAnchorEl)}
              onClick={openCreateMenu}
              endIcon={<Icon path={mdiMenuDown} size="18px" />}
            >
              New
            </Button>
            <Menu
              anchorEl={createAnchorEl}
              open={Boolean(createAnchorEl)}
              onClose={closeCreateMenu}
              classes={{ paper: styles['create-menu'], root: styles.menuRoot }}
            >
              <MenuItem onClick={startNewChat('image')} className={styles['dd-item']}>
                <Icon path={mdiImageOutline} size="20px" />
                Generate image
              </MenuItem>
              <MenuItem onClick={startNewChat('text')} className={styles['dd-item']}>
                <Icon path={mdiTextBoxOutline} size="20px" />
                Generate caption
              </MenuItem>
            </Menu>
          </>
        )}
        <TextField
          variant="standard"
          placeholder="Search your AI chats..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          classes={{ root: styles['search-box'] }}
          slotProps={{
            input: {
              className: styles['search-input'],
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Icon path={mdiMagnify} size="18px" className={styles['icon-search']} />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" className={styles['clear-btn']} onClick={() => setFilter('')}>
                    <Icon path={mdiClose} size="16px" className={styles['icon-clear']} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          }}
          fullWidth
        />
      </div>

      {loading && <div className={styles.empty}><CircularProgress size={20} /></div>}

      {!loading && !error && conversations.length === 0 && (
        <div className={styles['empty-state']}>
          <Icon path={mdiMessageTextOutline} size="48px" className={styles['empty-icon']} />
          <p className={styles['empty-title']}>No conversations yet</p>
          <p className={styles['empty-subtitle']}>
            {onNewChat
              ? <span className={styles['empty-link']} role="button" onClick={startNewChat('image')}>Start a new AI chat</span>
              : 'Start a new AI chat'}
            {' '}to brainstorm ideas, draft posts, and generate images.
          </p>
        </div>
      )}

      {!loading && !error && conversations.length > 0 && filtered.length === 0 && (
        <div className={styles.empty}>No chats match "{filter.trim()}".</div>
      )}

      {!loading && error && (
        <div className={styles.error}>
          <span>{error}</span>
          <span className={styles.retry} role="button" onClick={fetchChats}>
            Retry
          </span>
        </div>
      )}

      <List disablePadding className={styles.list}>
        {filtered.map((conv) => (
          <ListItem
            key={conv.id}
            disableGutters
            onClick={() => onConversationSelect?.(conv)}
            className={styles['list-item']}
          >
            <Icon
              path={conv.kind === 'image' ? mdiImageOutline : mdiTextBoxOutline}
              size="20px"
              className={styles['item-icon']}
            />
            <div className={styles['item-content']}>
              {conv.title && (
                <p className={`${styles['item-title']} text-ellipsis`} title={conv.title}>{conv.title}</p>
              )}
              <p className={styles['item-text']}>{firstUserMessage(conv)}</p>
            </div>
            <ConfirmDialog message="Delete this conversation?" variant="danger" confirmLabel="Delete">
              {(confirm) => (
                <ActionsStrip
                  className={styles['actions-overlay']}
                  actions={[
                    {
                      icon: <Icon path={mdiClose} size="18px" />,
                      tooltip: 'Delete',
                      onClick: confirm(() => delete$.current.next(conv.id))
                    }
                  ]}
                />
              )}
            </ConfirmDialog>
          </ListItem>
        ))}
      </List>
    </div>
  )
}

export default AIConversationsList
